import { ContentWorkflow, ReviewDecision } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import constants from '../../../common/constants';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import { Alert, Checkbox, Link } from '@amzn/awsui-components-core';

interface Props {
  isContentUnchanged: boolean;
  onSaveChanges: (unlock: boolean) => Promise<any>;
  onBeforeSaving?: () => Promise<boolean>;
  onSubmitForReview: () => Promise<any>;
  validateSubmitForReview: () => Promise<boolean>;
  fullwidth?: boolean;
  disabled: boolean;
  workflow?: ContentWorkflow;
}

const SubmitForReviewControl: React.FunctionComponent<Props> = (props: Props) => {
  const {
    isContentUnchanged,
    onBeforeSaving,
    onSaveChanges,
    onSubmitForReview,
    validateSubmitForReview,
    fullwidth,
    disabled,
  } = props;
  const [checked, setChecked] = React.useState<boolean>(false);

  // Saving changes won't leave the editing page (which is different from the Save and close button).
  // This is specific for submitting for review, so it's implemented separately in this component.
  const saveChanges = async () => {
    if (!onBeforeSaving || (await onBeforeSaving())) {
      if (onSaveChanges) {
        await onSaveChanges(false);
      }
    }
  };

  const submitForReview = async () => {
    if (!isContentUnchanged) {
      await saveChanges();
    }
    await onSubmitForReview();
  };

  const submitForReviewButton = (
    <ModalDialogButtonV3
      variant={'primary'}
      header={constants.SEND_FOR_REVIEW_MODAL_HEADER}
      onBeforeOpenModal={validateSubmitForReview}
      onSubmit={submitForReview}
      submitButtonText={constants.OK_BUTTON_TEXT}
      buttonText={constants.SEND_FOR_REVIEW_BUTTON_TEXT}
      disabled={disabled}
      fullwidth={!!fullwidth}
      inputValid={checked}
      dataTestId="right-column-edit-action-submit-for-review-modal"
    >
      <Alert statusIconAriaLabel="Warning" type="warning" header="Plagiarism check">
        <Checkbox checked={checked} onChange={({ detail }) => setChecked(detail.checked)}>
          I certify that my content is original. I understand that I can reuse text, code, and
          images only from{' '}
          <Link href="https://docs.aws.amazon.com/" external>
            AWS service documentation
          </Link>{' '}
          and from{' '}
          <Link href="https://aws.amazon.com/" external>
            AWS marketing pages
          </Link>
          . I understand that I should <i>not</i> reuse content from any other internal or external
          sources, such as from{' '}
          <Link href="https://aws.amazon.com/blogs/" external>
            AWS blogs
          </Link>{' '}
          or third-party sites. This constitutes <b>plagiarism</b>. Plagiarism poses severe
          reputational and legal risk to AWS. I understand that if I submit plagiarized content, AWS
          Prescriptive Guidance can revoke my permissions to author and review in APG Library and
          may escalate to appropriate teams.
        </Checkbox>
      </Alert>
      <br />
      {constants.SEND_FOR_REVIEW_MODAL_DESCRIPTION_EDIT_MODE}
    </ModalDialogButtonV3>
  );

  const publishButton = (
    <div>
      <ModalDialogButtonV3
        variant="normal"
        header={constants.PUBLISH_BUTTON_TEXT}
        text={constants.PUBLISH_MODAL_CONTENT}
        onBeforeOpenModal={validateSubmitForReview}
        onSubmit={submitForReview}
        submitButtonText={constants.PUBLISH_BUTTON_TEXT}
        buttonText={constants.PUBLISH_BUTTON_TEXT}
        disabled={disabled}
        fullwidth={!!fullwidth}
      />
    </div>
  );

  if (
    props.workflow?.skipDraftReview &&
    props.workflow?.lastReviewDecision !== ReviewDecision.DISAPPROVE
  ) {
    return publishButton;
  }

  return submitForReviewButton;
};

export default SubmitForReviewControl;
